
import React, { useEffect, useRef } from 'react';

const PortalBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrame: number;
    let particles: { x: number; y: number; size: number; speed: number; opacity: number; color: string }[] = [];

    const init = () => {
      const w = window.innerWidth || 800;
      const h = window.innerHeight || 600;
      canvas.width = w;
      canvas.height = h;
      particles = [];
      const colors = ['#ffffff', '#60a5fa', '#a855f7', '#ec4899'];
      const particleCount = w < 768 ? 80 : 200; // Reduce particles on mobile
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * w,
          y: Math.random() * h,
          size: Math.random() * (w < 768 ? 1.2 : 1.5),
          speed: Math.random() * 0.2 + 0.05,
          opacity: Math.random() * 0.5 + 0.1,
          color: colors[Math.floor(Math.random() * colors.length)]
        });
      }
    };

    const draw = () => {
      // Deep Space Fade
      ctx.fillStyle = '#020617';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const time = Date.now() * 0.001;

      // Draw Galaxy Core Glow
      const pulse = Math.sin(time * 0.5) * 20;
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 400 + pulse);
      gradient.addColorStop(0, 'rgba(88, 28, 135, 0.2)');
      gradient.addColorStop(0.5, 'rgba(30, 58, 138, 0.1)');
      gradient.addColorStop(1, 'rgba(2, 6, 23, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw Stars/Particles
      particles.forEach(p => {
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.opacity * (0.5 + Math.sin(time + p.x) * 0.5);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();

        p.y -= p.speed;
        if (p.y < 0) p.y = canvas.height;
      });
      ctx.globalAlpha = 1.0;

      // Draw Central Singularity
      const coreSize = 2 + Math.sin(time * 2) * 1;
      ctx.shadowBlur = 40 + pulse;
      ctx.shadowColor = '#60a5fa';
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(centerX, centerY, coreSize, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;

      animationFrame = requestAnimationFrame(draw);
    };

    init();
    draw();

    window.addEventListener('resize', init);
    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', init);
    };
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 z-0 pointer-events-none" />;
};

export default PortalBackground;
